import React, { useMemo } from "react";
import { RegisterOptions, FieldErrors, UseFormRegister, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
    name: string;
    register: UseFormRegister<FieldValues>;
    errors: FieldErrors<FieldValues>;
    options?: RegisterOptions<FieldValues, string> | undefined;
    isErrorThrown?: boolean;
    isOptional?: boolean;
    type?: "text" | "textarea";
    disabled?: boolean;
    autoFocus?: boolean;
    /** Renders the larger form-control-lg variant (used for single-field screens). */
    large?: boolean;
    /** Accessible label; falls back to the placeholder text. */
    label?: string;
    onPhoneChange?: (phone: string) => void;
}

const InputField = (props: InputFieldProps) => {
    const { t } = useTranslation();

    const error = useMemo(()=>{
      if(props.errors[props.name]?.type==='required'){
        return t('REQUIRED')
      }
      return props.errors[props.name]?.message as string;
    }, [props.errors[props.name]?.message, props.errors[props.name], t])

    const hasError = !!props.errors[props.name];
    const controlClasses = [
      'form-control',
      props.large ? 'form-control-lg' : '',
      hasError ? 'error' : '',
    ].filter(Boolean).join(' ');

  return (
    <div className='form-group u-mb-2'>
      <label htmlFor={props.name} className='u-mb-1 visually-hidden'>
        {props.label || props.placeholder}
      </label>

      {(!props.type || props.type==='text') &&
        <input
          id={props.name}
          autoFocus={props.autoFocus || false}
          disabled={props.disabled || false}
          className={controlClasses}
          defaultValue={props.defaultValue}
          {...props.register(props.name, {...props.options})}
          placeholder={props.placeholder}
          aria-invalid={hasError}
        />
      }

      {props.type==='textarea' &&
        <textarea
          id={props.name}
          rows={3}
          autoFocus={props.autoFocus || false}
          placeholder={props.placeholder}
          disabled={props.disabled || false}
          className={controlClasses}
          {...props.register(props.name, {...props.options})}
          aria-invalid={hasError}
        />
      }

      { error && <span className="u-color-error small" role="alert" aria-live="assertive">{error}</span> }
    </div>
  )
};

export default InputField;
